<template>
  <div v-if="shouldShow" class="query-error-overlay" @click.self="dismiss">
    <div class="query-error-modal">
      <div class="query-error-modal__body">
        <i class="fas fa-exclamation-circle query-error-modal__icon"></i>
        <p class="query-error-modal__title">{{ t('apiErrorTitle') || t('error') }}</p>
        <p v-if="!isSilentBusinessError" class="query-error-modal__status">
          {{ t('apiStatusCode') }}: {{ statusCodeText }}
        </p>
        <div v-if="failedEndpoints.length" class="query-error-modal__endpoints">
          <div class="query-error-modal__endpoint-list">
            <span
              v-for="item in failedEndpoints"
              :key="item.text"
              class="query-error-modal__endpoint"
              :title="item.text"
            >
              {{ item.text }}
            </span>
          </div>
        </div>
        <p class="query-error-modal__description">{{ errorMessage }}</p>
        <p v-if="isCordovaApp" class="query-error-modal__app-info">
          <span>{{ t('common.version_label') }}: {{ appVersion }}</span>
          <span>{{ t('common.build_label') }}: {{ appBuild }}</span>
        </p>
        <button class="query-error-modal__button btn btn-link" type="button" @click="dismiss">
          {{ t('close') || t('enter_again') || 'OK' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { extractApiEndpoint, extractApiStatusCode } from '@/utils/apiError';
import { useDebugStore } from '@/stores/debug';
import { useDeviceStore } from '@/stores/device';

const props = defineProps({
  error: {
    type: [Array, Object, String],
    default: null
  }
});

const { t } = useI18n();
const debugStore = useDebugStore();
const deviceStore = useDeviceStore();
const dismissed = ref(false);
const isCordovaApp = computed(() =>
  import.meta.env.VITE_APP_PLATFORM === 'cordova' || deviceStore.isCordovaReady
);
const appVersion = computed(() => String(deviceStore.version || '—'));
const appBuild = computed(() => String(deviceStore.versionCode || '—'));

const errors = computed(() => {
  const list = Array.isArray(props.error) ? props.error : [props.error];
  return list.filter(Boolean);
});

const firstError = computed(() => {
  return errors.value.find((error) => extractApiStatusCode(error) > 0) || errors.value[0] || null;
});

const statusCode = computed(() => {
  return extractApiStatusCode(firstError.value);
});

const isSilentBusinessError = computed(() => firstError.value?.code === 'EMPTY_PERIODS');

const errorMessage = computed(() => {
  const error = firstError.value;
  if (!error) return '';
  if (typeof error === 'string') return error;

  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data?.message) return String(data.message);
  if (Array.isArray(data) && data[0]?.message) return String(data[0].message);
  if (Array.isArray(data?.errors) && data.errors[0]?.message) {
    return String(data.errors[0].message);
  }
  if (error?.message) return String(error.message);
  return t('unknown_error');
});

const statusCodeText = computed(() =>
  statusCode.value > 0 ? String(statusCode.value) : t('apiStatusUnknown')
);

const failedEndpoints = computed(() => {
  if (isSilentBusinessError.value) return [];

  const seen = new Set();
  return errors.value
    .map((error) => {
      const endpoint = extractApiEndpoint(error);
      return {
        endpoint,
        text: endpoint
      };
    })
    .filter((item) => item.endpoint)
    .filter((item) => {
      if (seen.has(item.text)) return false;
      seen.add(item.text);
      return true;
    });
});

const resolvedText = computed(() => {
  if (!firstError.value) return '';
  return `${statusCode.value || ''}:${failedEndpoints.value.map((item) => item.text).join('|')}:${errorMessage.value}`;
});

const shouldShow = computed(() =>
  debugStore.apiErrorsEnabled && Boolean(resolvedText.value) && !dismissed.value
);

const displayKey = computed(() =>
  debugStore.apiErrorsEnabled ? resolvedText.value : ''
);

watch(
  () => displayKey.value,
  () => {
    dismissed.value = false;
  }
);

function dismiss() {
  dismissed.value = true;
}
</script>

<style scoped>
.query-error-overlay {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
}

.query-error-modal {
  width: 90%;
  max-width: 460px;
  border-radius: 8px;
  background: #fff;
  padding: 16px;
}

.query-error-modal__body {
  margin: 24px 0;
  text-align: center;
}

.query-error-modal__icon {
  font-size: 72px;
  color: #eb5757;
  margin-bottom: 25px;
}

.query-error-modal__title {
  color: #eb5757;
  font-weight: bold;
  font-size: 24px;
  line-height: 1.15;
  margin-bottom: 10px;
}

.query-error-modal__status {
  color: #4f4f4f;
  font-weight: 700;
  margin: 0 24px 8px;
}

.query-error-modal__description {
  color: #828282;
  margin: 0 24px 16px;
  overflow-wrap: anywhere;
}

.query-error-modal__app-info {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin: 0 24px 16px;
  color: #828282;
  font-size: 12px;
  line-height: 16px;
}

.query-error-modal__endpoints {
  margin: 0 24px 14px;
  padding: 10px 12px;
  border: 1px solid #f2caca;
  border-radius: 6px;
  background: #fff7f7;
  text-align: left;
}

.query-error-modal__endpoint-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.query-error-modal__endpoint {
  display: inline-block;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 4px;
  background: #ffffff;
  color: #eb5757;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.query-error-modal__button {
  width: 100%;
  min-height: 32px;
  font-weight: bold;
  font-size: 16px;
  line-height: 19px;
  color: #2f80ed;
}

@media screen and (max-width: 768px) {
  .query-error-modal {
    width: 100%;
    padding: 14px;
  }

  .query-error-modal__body {
    margin: 18px 0;
  }

  .query-error-modal__icon {
    font-size: 64px;
    margin-bottom: 20px;
  }

  .query-error-modal__title {
    font-size: 22px;
  }

  .query-error-modal__status,
  .query-error-modal__endpoints,
  .query-error-modal__description,
  .query-error-modal__app-info {
    margin-left: 12px;
    margin-right: 12px;
  }
}
</style>
